// 🔧 content_key_helpers.js
// ✅ Purpose: Canonical helper utilities for creating, parsing, and validating Content Engine keys
// ✅ Features:
// - Enforces approved Content Engine key format: area.section.element
// - Prevents random manual key formatting across the app
// - Supports page bundles by allowing easy area extraction
// - Keeps future AI, localization, bundle generation, admin search, and migration tools aligned
// - Uses safe normalization so content keys remain Firestore-friendly and predictable

/**
 * Separator used for all Content Engine keys.
 *
 * Approved format:
 * area.section.element
 */
export const CONTENT_KEY_SEPARATOR = ".";

/**
 * Basic allowed key segment pattern.
 *
 * We intentionally keep segments lowercase with numbers and underscores.
 *
 * Good:
 * event_home
 * hero
 * title
 *
 * Avoid:
 * Event Home
 * hero-title
 * title!
 */
export const CONTENT_KEY_SEGMENT_PATTERN = /^[a-z0-9_]+$/;

/**
 * Normalizes a raw value into a safe content key segment.
 *
 * Examples:
 * "Event Home" -> "event_home"
 * "Hero Title!" -> "hero_title"
 * "  Vote Now  " -> "vote_now"
 */
export function normalizeContentKeySegment(value = "") {
  // Treat null and undefined as empty values.
  // This prevents accidental key segments like "null" or "undefined".
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Validates a single content key segment.
 *
 * Returns true only when the segment follows the approved format.
 */
export function isValidContentKeySegment(segment = "") {
  return CONTENT_KEY_SEGMENT_PATTERN.test(String(segment));
}

/**
 * Builds the canonical content key.
 *
 * Required structure:
 * area.section.element
 *
 * Example:
 * buildContentKey({
 *   area: "event_home",
 *   section: "hero",
 *   element: "title",
 * })
 *
 * Result:
 * event_home.hero.title
 */
export function buildContentKey({ area, section, element }) {
  const normalizedArea = normalizeContentKeySegment(area);
  const normalizedSection = normalizeContentKeySegment(section);
  const normalizedElement = normalizeContentKeySegment(element);

  if (
    !normalizedArea ||
    !normalizedSection ||
    !normalizedElement
  ) {
    console.error("[Content Engine] Missing content key segment.", {
      area,
      section,
      element,
      normalizedArea,
      normalizedSection,
      normalizedElement,
    });

    throw new Error(
      "Content Engine keys require area, section, and element."
    );
  }

  return [
    normalizedArea,
    normalizedSection,
    normalizedElement,
  ].join(CONTENT_KEY_SEPARATOR);
}

/**
 * Parses a canonical content key into its parts.
 *
 * Example:
 * event_home.hero.title
 *
 * Returns:
 * {
 *   area: "event_home",
 *   section: "hero",
 *   element: "title",
 * }
 */
export function parseContentKey(contentKey = "") {
  const parts = String(contentKey).split(CONTENT_KEY_SEPARATOR);

  if (parts.length !== 3) {
    console.error("[Content Engine] Invalid content key format.", {
      contentKey,
      expectedFormat: "area.section.element",
    });

    return {
      area: "",
      section: "",
      element: "",
      isValid: false,
    };
  }

  const [area, section, element] = parts;

  const isValid =
    isValidContentKeySegment(area) &&
    isValidContentKeySegment(section) &&
    isValidContentKeySegment(element);

  return {
    area,
    section,
    element,
    isValid,
  };
}

/**
 * Validates a complete content key.
 *
 * Approved format:
 * area.section.element
 */
export function isValidContentKey(contentKey = "") {
  const parsed = parseContentKey(contentKey);

  return parsed.isValid === true;
}

/**
 * Extracts the area from a content key.
 *
 * This is used by bundle builders and resolvers.
 *
 * Example:
 * event_home.hero.title -> event_home
 */
export function getAreaFromContentKey(contentKey = "") {
  const parsed = parseContentKey(contentKey);

  return parsed.area || "";
}

/**
 * Extracts the section from a content key.
 *
 * Example:
 * event_home.hero.title -> hero
 */
export function getSectionFromContentKey(contentKey = "") {
  const parsed = parseContentKey(contentKey);

  return parsed.section || "";
}

/**
 * Extracts the element from a content key.
 *
 * Example:
 * event_home.hero.title -> title
 */
export function getElementFromContentKey(contentKey = "") {
  const parsed = parseContentKey(contentKey);

  return parsed.element || "";
}

/**
 * Returns true when the content key belongs to the supplied area.
 *
 * Useful for admin filtering, migration scripts, and bundle generation.
 */
export function contentKeyBelongsToArea(contentKey = "", area = "") {
  const parsed = parseContentKey(contentKey);
  const normalizedArea = normalizeContentKeySegment(area);

  return parsed.isValid && parsed.area === normalizedArea;
}

/**
 * Safely builds a content key but returns a fallback value instead of throwing.
 *
 * This can be useful in migration scripts or admin tools where we want to
 * continue processing and report bad rows instead of crashing immediately.
 */
export function tryBuildContentKey({ area, section, element, fallback = "" }) {
  try {
    return buildContentKey({
      area,
      section,
      element,
    });
  } catch (error) {
    console.error("[Content Engine] Failed to build content key.", {
      area,
      section,
      element,
      error,
    });

    return fallback;
  }
}